import React, { useEffect, useState } from 'react'
import { Button } from '@/components/ui/Button'
import {
  getProductionMonthHistoryApi,
  type ProductionMonthHistoryItem,
} from '@/services/productionOrders'
import { formatDocumentError } from '@/utils/documentErrorMessages'

export interface MonthHistoryModalProps {
  isOpen: boolean
  month: string
  onClose: () => void
}

export function MonthHistoryModal({
  isOpen,
  month,
  onClose,
}: MonthHistoryModalProps): React.ReactElement | null {
  const [history, setHistory] = useState<ProductionMonthHistoryItem[]>([])
  const [loading, setLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen || !month || month === 'unscheduled') return

    setLoading(true)
    setErrorMsg(null)
    getProductionMonthHistoryApi(month)
      .then((data) => setHistory(data))
      .catch((err) => setErrorMsg(formatDocumentError(err).message))
      .finally(() => setLoading(false))
  }, [isOpen, month])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h3 className="font-bold text-slate-800 text-base">
            Nhật ký phê duyệt & thay đổi: <span className="font-mono text-emerald-800">{month}</span>
          </h3>
          <button
            type="button"
            className="text-slate-400 hover:text-slate-600 font-bold"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {errorMsg && (
            <div className="mb-4 rounded border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
              {errorMsg}
            </div>
          )}

          {loading ? (
            <p className="py-8 text-center text-sm text-slate-400">Đang tải lịch sử...</p>
          ) : history.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-500 italic">
              Chưa có nhật ký hoạt động nào cho tháng này.
            </p>
          ) : (
            <div className="relative border-l-2 border-slate-200 ml-3 space-y-4">
              {history.map((item) => {
                const dateStr = new Date(item.at).toLocaleString('vi-VN')
                return (
                  <div key={item.id} className="relative pl-5">
                    {/* Timeline bullet */}
                    <div className="absolute -left-[9px] top-1 h-4 w-4 rounded-full border-2 border-white bg-emerald-600 shadow" />

                    <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                        <span className="font-bold text-slate-800">
                          {item.action === 'APPROVE'
                            ? 'Duyệt khóa kế hoạch tháng'
                            : item.action === 'REOPEN'
                            ? 'Mở lại tháng kế hoạch'
                            : item.action}
                        </span>
                        <span className="text-slate-400 font-mono">{dateStr}</span>
                      </div>
                      <div className="mt-1 text-xs text-slate-600">
                        Người thực hiện:{' '}
                        <strong className="text-slate-800">{item.actor || 'system'}</strong>
                      </div>

                      {item.detail && (
                        <div className="mt-2 rounded bg-white p-2 text-xs border border-slate-200 font-mono text-slate-700">
                          {item.detail.reason ? (
                            <p className="font-sans italic text-slate-800">
                              Lý do: &quot;{String(item.detail.reason)}&quot;
                            </p>
                          ) : null}
                          {item.detail.plan_lines_count != null ? (
                            <p className="text-slate-500">
                              Số dòng kế hoạch: {String(item.detail.plan_lines_count)}
                            </p>
                          ) : null}
                          {item.detail.changes ? (
                            <pre className="text-xs overflow-x-auto whitespace-pre-wrap">
                              {JSON.stringify(item.detail.changes, null, 2)}
                            </pre>
                          ) : null}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <div className="flex justify-end border-t border-slate-200 bg-slate-50 px-6 py-3">
          <Button size="sm" variant="secondary" onClick={onClose}>
            Đóng
          </Button>
        </div>
      </div>
    </div>
  )
}
